import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, useParams, useLocation } from "react-router-dom";
import { TemplateService } from "@/services/templateService";
import { Template } from "@/types/models/template";

interface PageSelection {
  pageId: string;
  selected: boolean;
  customData?: {
    title: string;
    slug: string;
    description: string;
    featured_image: string; // Asset ID
  };
}

export const useTemplateSelection = () => {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const templateId = searchParams.get("templateId");
  const templateSlug = searchParams.get("templateSlug");
  const templateName = searchParams.get("templateName");

  const [template, setTemplate] = useState<Template | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageSelections, setPageSelections] = useState<PageSelection[]>([]);
  const [showCustomization, setShowCustomization] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<Map<string, string>>(
    new Map(),
  );

  const [uploadingImages, _setUploadingImages] = useState<Set<string>>(
    new Set(),
  );

  const fetchTemplate = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let templateData: Template;
      if (templateId) {
        console.log("Fetching template with ID:", templateId);
        templateData = await TemplateService.getTemplateById(templateId);
      } else if (templateSlug) {
        console.log("Fetching template with slug:", templateSlug);
        templateData = await TemplateService.getTemplateBySlug(templateSlug);
      } else {
        throw new Error("No template ID or slug provided");
      }

      console.log("Template data received:", templateData);
      setTemplate(templateData);
      initializePageSelections(templateData);
    } catch (error) {
      console.error("Error fetching template:", error);
      setError("Failed to load template. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [templateId, templateSlug]);

  useEffect(() => {
    if (templateId || templateSlug) {
      fetchTemplate();
    } else {
      setError("No template ID or slug provided");
      setLoading(false);
    }
  }, [fetchTemplate]);

  const initializePageSelections = (templateData: Template) => {
    // When fetching template by ID, pages are always included
    if (!templateData.pages || templateData.pages.length === 0) {
      setPageSelections([]);
      return;
    }

    const selections = templateData.pages.map((page) => ({
      pageId: page.id,
      selected: true,
      customData: {
        title: page.title || page.name,
        slug: page.slug,
        description: page.description || "",
        featured_image: page.featured_image_asset?.id || "",
      },
    }));
    setPageSelections(selections);
  };

  const togglePageSelection = (pageId: string) => {
    setPageSelections((prev) =>
      prev.map((selection) =>
        selection.pageId === pageId
          ? { ...selection, selected: !selection.selected }
          : selection,
      ),
    );
  };

  const updateCustomData = (pageId: string, field: string, value: string) => {
    setPageSelections((prev) =>
      prev.map((selection) =>
        selection.pageId === pageId
          ? {
              ...selection,
              customData: {
                ...selection.customData!,
                [field]: value,
              },
            }
          : selection,
      ),
    );
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  const handleTitleChange = (pageId: string, title: string) => {
    const selection = pageSelections.find((s) => s.pageId === pageId);
    const currentSlug = selection?.customData?.slug || "";
    const originalPage = template?.pages?.find((p) => p.id === pageId);
    const originalSlug = originalPage?.slug || "";

    const shouldUpdateSlug =
      currentSlug === generateSlug(selection?.customData?.title || "") ||
      currentSlug === originalSlug ||
      currentSlug === "";

    updateCustomData(pageId, "title", title);
    if (shouldUpdateSlug) {
      updateCustomData(pageId, "slug", generateSlug(title));
    }
  };

  const handleImageSelected = (
    pageId: string,
    selectedFile: { assetId: string; url: string },
  ) => {
    console.log("🖼️ Image selected:", { pageId, selectedFile });
    setUploadedImages((prev) => new Map(prev).set(pageId, selectedFile.url));
    updateCustomData(pageId, "featured_image", selectedFile.assetId);
  };

  const removeUploadedImage = (pageId: string) => {
    setUploadedImages((prev) => {
      const newMap = new Map(prev);
      newMap.delete(pageId);
      return newMap;
    });
    updateCustomData(pageId, "featured_image", "");
  };

  const selectedPages = pageSelections.filter((s) => s.selected);
  const needsCustomization = selectedPages.length === 1;

  const handleContinue = () => {
    if (needsCustomization && !showCustomization) {
      setShowCustomization(true);
      return;
    }

    const selectedPageIds = selectedPages.map((s) => s.pageId);
    const customizations = selectedPages.reduce(
      (acc, selection) => {
        if (selection.customData) {
          acc[selection.pageId] = selection.customData;
        }
        return acc;
      },
      {} as Record<
        string,
        {
          title?: string;
          slug?: string;
          description?: string;
          featured_image?: string;
        }
      >,
    );

    const queryParams = new URLSearchParams({
      templateName: templateName || "",
      selectedPages: selectedPageIds.join(","),
      customizations: JSON.stringify(customizations),
    });

    if (templateId) {
      queryParams.set("templateId", templateId);
    } else if (templateSlug) {
      queryParams.set("templateSlug", templateSlug);
    }

    const { storeType, storeId } = params || {};
    if (storeType && storeId) {
      // detect if current path is within the onboarding subtree
      const inOnboarding = typeof window !== "undefined" && window.location.pathname.includes("/webbuilder/onboarding");
      const targetPath = inOnboarding ? `/${storeType}/${storeId}/webbuilder/onboarding/import` : `/${storeType}/${storeId}/webbuilder/import`;
      navigate(`${targetPath}?${queryParams.toString()}`);
    }
  };

  const handleBack = () => {
    if (showCustomization) {
      setShowCustomization(false);
      return;
    }
    navigate(-1);
  };

  return {
    template,
    loading,
    error,
    pageSelections,
    showCustomization,
    uploadingImages,
    uploadedImages,
    selectedPages,
    needsCustomization,
    templateName,
    togglePageSelection,
    updateCustomData,
    generateSlug,
    handleTitleChange,
    handleImageSelected,
    removeUploadedImage,
    handleContinue,
    handleBack,
  };
};
